/**
 * "May this account do X" for the UI.
 *
 * The list comes from `/api/me`, computed server-side by the same `capabilitiesFor`
 * the preHandlers use, so a button and the endpoint behind it cannot disagree.
 *
 * What this is for: not rendering doors that open onto a 403. A maintenance tech
 * should not see an "Add property" button, because a button that always fails is
 * worse than no button — it reads as a broken app rather than a rule.
 *
 * What this is NOT: protection. Deleting the `disabled` attribute in devtools gets
 * you a request that the preHandler refuses and the policy refuses behind it. Never
 * write a screen whose safety depends on a hook in this file.
 */

import { hasCapability, type Capability } from 'shared';
import { useSession } from './session.js';

export function useCapabilities(): readonly Capability[] {
  const { state } = useSession();
  return state.status === 'signed-in' ? state.me.actor.capabilities : [];
}

/** Single check. Returns false while the session is still loading — fail closed. */
export function useCan(capability: Capability): boolean {
  return hasCapability(useCapabilities(), capability);
}

/**
 * The signed-in actor, or null.
 *
 * A convenience so role-aware screens do not each repeat the `status !== 'signed-in'`
 * dance. Returns the actor rather than the whole `/api/me` payload, because a screen
 * that needs the counts should ask for them by name.
 */
export function useActor() {
  const { state } = useSession();
  return state.status === 'signed-in' ? state.me.actor : null;
}
